import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { USER_DIMENSIONS } from '@/api/types'
import { PlatformExportButton } from '@/features/analytics/PlatformExportButton'
import { AcquisitionTab } from '@/features/analytics/tabs/AcquisitionTab'
import { ContentTab } from '@/features/analytics/tabs/ContentTab'
import { GrowthTab } from '@/features/analytics/tabs/GrowthTab'
import { OperationsTab } from '@/features/analytics/tabs/OperationsTab'
import { UsersTab } from '@/features/analytics/tabs/UsersTab'
import { WorldsTab } from '@/features/analytics/tabs/WorldsTab'
import { PageHeader, SelectField, TabPanel, Tabs, type TabItem } from '@/ui'

/**
 * The analytics section.
 *
 * The dashboard at `/` stays the landing summary; this is where the deeper questions live. They
 * are tabs rather than pages because somebody reading these moves between them constantly —
 * "storage jumped" and "which accounts" are one thought — and separate routes would make that a
 * navigation problem.
 *
 * **Every selection is a search param.** The open tab, the range and the ranking all live in the
 * URL, so a view survives a reload and can be pasted to somebody else. That is the rule §7 sets
 * for filters, and an analytics view is nothing but filters.
 */

const TABS = ['growth', 'acquisition', 'users', 'worlds', 'content', 'operations'] as const

type AnalyticsTab = (typeof TABS)[number]

const RANGES = [7, 30, 90, 365] as const

/** The tabs whose figures are bounded by a range rather than describing right now. */
const RANGED_TABS = new Set<AnalyticsTab>(['growth', 'acquisition', 'users', 'operations'])

const searchSchema = z.object({
  tab: z.enum(TABS).default('growth'),
  days: z.coerce
    .number()
    .pipe(z.union([z.literal(7), z.literal(30), z.literal(90), z.literal(365)]))
    .default(30),
  by: z.enum(['visits', 'rating', 'active']).default('visits'),
  dimension: z.enum(USER_DIMENSIONS).default('country'),
})

export const Route = createFileRoute('/_authed/analytics/')({
  validateSearch: searchSchema,
  component: AnalyticsPage,
})

function AnalyticsPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const tabs: TabItem[] = TABS.map((id) => ({
    id,
    label: t(`analytics.tab.${id}` as 'analytics.tab.worlds'),
  }))

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('analytics.title')} description={t('analytics.description')} />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <Tabs
          tabs={tabs}
          activeId={search.tab}
          onChange={(id) => {
            void navigate({ search: (previous) => ({ ...previous, tab: id as AnalyticsTab }) })
          }}
        />

        {/* Shown only on the tabs a range actually changes. Worlds and Content are current-state
            views, and a filter that visibly does nothing teaches people to distrust every other
            one on the screen. */}
        <PlatformExportButton days={search.days} />

        {RANGED_TABS.has(search.tab) ? (
          <div className="w-40">
            <SelectField
              label={t('analytics.range')}
              value={String(search.days)}
              onChange={(event) => {
                void navigate({
                  search: (previous) => ({ ...previous, days: Number(event.target.value) }),
                })
              }}
              options={RANGES.map((days) => ({
                value: String(days),
                label: t(`range.${String(days)}d` as 'range.30d'),
              }))}
            />
          </div>
        ) : null}
      </div>

      <TabPanel id={search.tab}>
        {search.tab === 'worlds' ? (
          <WorldsTab
            ranking={search.by}
            onRankingChange={(by) => {
              void navigate({ search: (previous) => ({ ...previous, by }) })
            }}
          />
        ) : null}

        {search.tab === 'growth' ? <GrowthTab days={search.days} /> : null}
        {search.tab === 'acquisition' ? <AcquisitionTab days={search.days} /> : null}

        {search.tab === 'users' ? (
          <UsersTab
            days={search.days}
            dimension={search.dimension}
            onDimensionChange={(dimension) => {
              void navigate({ search: (previous) => ({ ...previous, dimension }) })
            }}
          />
        ) : null}

        {search.tab === 'content' ? <ContentTab /> : null}
        {search.tab === 'operations' ? <OperationsTab days={search.days} /> : null}
      </TabPanel>
    </div>
  )
}
